import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { formatCurrency, highlightSupportWord, parseInvoices } from '../MessageBubbleHelpers';

// Shared "label above value" stat block used for every field in the invoice detail row
// (Contract ID, Due Date, Next Due Date, Amount) — identical styling for all but the value's font weight.
const InvoiceStat: React.FC<{ label: string; value: React.ReactNode; minWidth: string; valueWeight?: number }> = ({
    label,
    value,
    minWidth,
    valueWeight = 600,
}) => (
    <Box sx={{ minWidth }}>
        <Typography
            variant="caption"
            sx={{
                color: colors.greyPreset[50],
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.66rem',
                letterSpacing: 0.4,
            }}
        >
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: valueWeight }}>
            {value}
        </Typography>
    </Box>
);

export const InvoicesCard: React.FC<{ invoices: ReturnType<typeof parseInvoices> }> = ({ invoices }) => {
    if (!invoices) return null;
    return (
        <Box>
            <Typography
                variant="body2"
                sx={{ mb: 1, color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.45 }}
            >
                {highlightSupportWord((invoices.intro || 'Open invoices').replace(/:\s*$/, ''))}
            </Typography>

            <Stack gap={0.95}>
                {invoices.items.map((inv, idx) => {
                    const rowKey = `${inv.contractId}-${inv.invoiceNumber}-${idx}`;
                    const hasNextDueDate = Boolean((inv.nextDueDate || '').trim());
                    const hasRemainingDue = Boolean((inv.remainingDue || '').trim());
                    const hasLateFee = Boolean((inv.lateFee || '').trim());
                    const hasExtraField = hasRemainingDue || hasLateFee;

                    return (
                        <Box
                            key={rowKey}
                            sx={{
                                borderRadius: '11px',
                                backgroundColor: colors.fullWhite,
                                border: `1px solid ${colors.greyPreset[20]}`,
                                boxShadow: '0 1px 6px rgba(16, 24, 40, 0.06)',
                                px: 1.1,
                                py: 0.95,
                            }}
                        >
                            <Stack gap={0.75}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem' }}
                                    >
                                        Invoice #{inv.invoiceNumber || '-'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            px: 0.7,
                                            py: 0.15,
                                            borderRadius: '999px',
                                            backgroundColor: inv.overdue ? '#FFF1F0' : '#EFF6FF',
                                            color: inv.overdue ? '#CF1322' : '#1D4ED8',
                                            fontWeight: 700,
                                            fontSize: '0.74rem',
                                        }}
                                    >
                                        {inv.overdue ? 'Past Due' : 'Due'}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" gap={1.1} flexWrap="wrap">
                                    <InvoiceStat label="Contract ID" value={inv.contractId || '-'} minWidth="120px" />
                                    <InvoiceStat label="Due Date" value={inv.dueDate || '-'} minWidth="112px" />
                                    {hasNextDueDate && (
                                        <InvoiceStat
                                            label="Next Due Date"
                                            value={inv.nextDueDate || '-'}
                                            minWidth="112px"
                                        />
                                    )}
                                    <InvoiceStat
                                        label="Amount"
                                        value={formatCurrency(inv.amountDue)}
                                        minWidth="120px"
                                        valueWeight={700}
                                    />
                                </Stack>

                                {hasExtraField && (
                                    <Stack direction="row" gap={0.8} flexWrap="wrap" sx={{ pt: 0.25 }}>
                                        {hasRemainingDue && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    px: 0.7,
                                                    py: 0.2,
                                                    borderRadius: '6px',
                                                    backgroundColor: '#F8FAFC',
                                                    border: `1px solid ${colors.greyPreset[20]}`,
                                                    color: colors.greyPreset[60],
                                                    fontWeight: 700,
                                                    fontSize: '0.74rem',
                                                }}
                                            >
                                                Remaining Due: {formatCurrency(inv.remainingDue || '0')}
                                            </Typography>
                                        )}
                                        {hasLateFee && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    px: 0.7,
                                                    py: 0.2,
                                                    borderRadius: '6px',
                                                    backgroundColor: '#FFF7E6',
                                                    border: '1px solid #FFE7BA',
                                                    color: '#AD6800',
                                                    fontWeight: 700,
                                                    fontSize: '0.74rem',
                                                }}
                                            >
                                                Late Fee: {formatCurrency(inv.lateFee || '0')}
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>

            {invoices.followUp && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 1.5,
                        color: colors.greyPreset[60],
                        whiteSpace: 'pre-line',
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                    }}
                >
                    {highlightSupportWord(invoices.followUp)}
                </Typography>
            )}
        </Box>
    );
};
